import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../../data/firebase/firebaseConfig.js';
import '../../styles/location-picker.css';

const validCoordinates = race => typeof race?.lat === 'number' && Number.isFinite(race.lat) && Math.abs(race.lat) <= 90 && typeof race?.lng === 'number' && Number.isFinite(race.lng) && Math.abs(race.lng) <= 180;

/** Explicit place search and confirmation. Typing never makes network requests. */
export class LocationPicker {
  constructor(input) {
    this.input = input;
    this._generation = 0;
    this._selection = null;
    this._search = httpsCallable(getFunctions(app), 'searchRaceLocations');
    this.element = document.createElement('div');
    this.element.className = 'location-picker';
    this.button = document.createElement('button');
    this.button.type = 'button';
    this.button.textContent = 'Search city';
    this.status = document.createElement('p');
    this.status.setAttribute('role', 'status');
    this.results = document.createElement('div');
    this.element.append(this.button, this.status, this.results);
    input.parentNode.appendChild(this.element);
    this._onInput = () => { this._generation++; this._selection = null; this.results.replaceChildren(); this.status.textContent = 'Search and confirm the place to map this race.'; this.button.disabled = false; };
    input.addEventListener('input', this._onInput);
    this.button.addEventListener('click', () => this.search());
  }

  reset(race = null) {
    this._generation++;
    this.results.replaceChildren();
    this.button.disabled = false;
    this._selection = validCoordinates(race) && race.location === this.input.value ? { location: race.location, lat: race.lat, lng: race.lng, country: race.country || null } : null;
    this.status.textContent = this._selection ? 'Saved map location. Search to choose a different place.' : 'Search and confirm the place to map this race.';
    if (this._selection) this.results.appendChild(this._mapLink(this._selection));
  }

  getSelection() {
    return this._selection?.location === this.input.value ? { ...this._selection } : null;
  }

  _mapLink(place) {
    const link = document.createElement('a');
    link.href = `https://www.openstreetmap.org/?mlat=${place.lat}&mlon=${place.lng}#map=12/${place.lat}/${place.lng}`;
    link.target = '_blank';
    link.rel = 'noopener noreferrer';
    link.textContent = `View ${place.lat.toFixed(4)}, ${place.lng.toFixed(4)} on OpenStreetMap`;
    return link;
  }

  async search() {
    const query = this.input.value.trim();
    if (query.length < 2 || query.length > 120) { this.status.textContent = 'Enter a city or place between 2 and 120 characters.'; return; }
    const generation = ++this._generation;
    const originalValue = this.input.value;
    this.button.disabled = true;
    this.results.replaceChildren();
    this.status.textContent = 'Searching places…';
    try {
      const response = await this._search({ query });
      if (generation !== this._generation || this.input.value !== originalValue) return;
      const places = (response.data?.results || []).filter(validCoordinates);
      this.status.textContent = places.length ? 'Choose the matching place. Map data © OpenStreetMap contributors.' : 'No places found. Try a city and country, or keep the text without a map location.';
      for (const place of places) {
        const row = document.createElement('div');
        row.className = 'location-picker__result';
        const name = document.createElement('p');
        name.textContent = place.location;
        const confirm = document.createElement('button');
        confirm.type = 'button';
        confirm.textContent = 'Use this place';
        confirm.addEventListener('click', () => {
          if (generation !== this._generation || this.input.value !== originalValue) return;
          // Keep the user's concise label (the form caps location length).
          this._selection = { location: originalValue, lat: place.lat, lng: place.lng, country: place.country || null };
          this._generation++;
          this.status.textContent = `Confirmed: ${place.location}`;
          this.results.replaceChildren(this._mapLink(place));
        });
        row.append(name, this._mapLink(place), confirm);
        this.results.appendChild(row);
      }
    } catch (error) {
      if (generation === this._generation) this.status.textContent = 'Location search is unavailable. Try again shortly, or save the location text without coordinates.';
    } finally {
      if (generation === this._generation) this.button.disabled = false;
    }
  }

  destroy() {
    this._generation++;
    this.input.removeEventListener('input', this._onInput);
    this.element.remove();
  }
}
